import { Injectable, EventEmitter } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  Validators,
  AbstractControl,
} from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class ReactiveFormHelperService {
  constructor(private fb: FormBuilder) {}

  setValidation(form: any, fields: any, isFinalization: boolean = false) {
    form.controls.forEach((control: AbstractControl) => {
      const group = control as FormGroup;
      const hasAnyValue = fields.some((field: any) => {
        const value = group.get(field)?.value;
        return (
          value !== null &&
          value !== undefined &&
          value !== 0 &&
          value.toString().trim() !== ''
        );
      });

      fields.forEach((field: any) => {
        const fieldControl = group.get(field);
        if (!fieldControl) return;

        if (hasAnyValue || isFinalization) {
          fieldControl.setValidators([Validators.required]);
        } else {
          fieldControl.clearValidators();
        }

        fieldControl.markAsPristine();
        // fieldControl.markAsUntouched();
        fieldControl.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  /**
   * this function add the validation for partial filled field
   *
   * @param group
   * @param fields
   * @param isFinalization
   */
  setValidationForGroup(
    group: FormGroup,
    fields: string[],
    isFinalization: boolean = false
  ) {
    const hasAnyValue = fields.some((field: string) => {
      const value = group.get(field)?.value;
      return (
        value !== null &&
        value !== undefined &&
        value !== 0 &&
        value.toString().trim() !== ''
      );
    });

    this.setOrClearValidationForEachField(
      group,
      fields,
      hasAnyValue,
      isFinalization
    );
  }

  clearValidation(form: any, fields: any) {
    form.controls.forEach((control: AbstractControl) => {
      const group = control as FormGroup;

      fields.forEach((field: any) => {
        const fieldControl = group.get(field);
        if (!fieldControl) return;

        fieldControl.clearValidators();
        fieldControl.markAsPristine();
        fieldControl.updateValueAndValidity({ emitEvent: false });
      });
    });
  }

  // for row wise validation - will not check whole form check only dirty row
  validationForDirtyRow(group: FormGroup, fields: string[]) {
    const hasAnyValue = fields.some((field: string) => {
      const value = group.get(field)?.value;
      return (
        value !== null &&
        value !== undefined &&
        value !== 0 &&
        value.toString().trim() !== ''
      );
    });

    fields.forEach((field: string) => {
      const fieldControl = group.get(field);
      if (!fieldControl) return;

      if (hasAnyValue) {
        fieldControl.setValidators([Validators.required]);
      } else {
        fieldControl.clearValidators();
      }

      fieldControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  setOrClearValidationForEachField(
    group: FormGroup,
    fields: string[],
    hasAnyValue: boolean,
    isFinalization: boolean = false
  ) {
    fields.forEach((field: string) => {
      const fieldControl = group.get(field);
      if (!fieldControl) return;

      if (hasAnyValue || isFinalization) {
        fieldControl.setValidators([Validators.required]);
      } else {
        fieldControl.clearValidators();
      }

      fieldControl.markAsPristine();
      // fieldControl.markAsUntouched();
      fieldControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * add validation for current row and return true or false if need to check for invalid other return true
   *
   * @param group
   * @param fields
   * @param isFinalization
   * @param checkInvalid
   * @returns
   */
  addNewRowValidation(
    group: FormGroup,
    fields: string[],
    isFinalization: boolean = false,
    checkInvalid: boolean = false
  ) {
    const hasAllValue = fields?.every((field: string) => {
      const value = group?.get(field)?.value;
      return (
        value !== null &&
        value !== undefined &&
        value !== 0 &&
        value?.toString()?.trim() !== ''
      );
    });

    this.setOrClearValidationForEachField(
      group,
      fields,
      !hasAllValue,
      isFinalization
    );

    if (checkInvalid) {
      if (group?.invalid) {
        group?.markAllAsTouched();
        return false;
      }
    }

    return true;
  }

  setAndCheckNewRowValidations(forms: FormArray, fields: string[]) {
    if (forms && forms.length > 0) {
      const last = forms.at(forms.length - 1) as FormGroup;
      if (!this.addNewRowValidation(last, fields, false, true)) {
        return false;
      }
    }

    return true;
  }

  resetValidations(forms: FormArray, fields: string[]) {
    this.clearValidation(forms, fields);

    // set new validation
    forms.controls.forEach((group, index) => {
      if (forms.length - 1 !== index) {
        this.addNewRowValidation(group as FormGroup, fields);
      }
    });
  }

  /**
   * Scroll to first invalid control
   * using the combination of formControlName and ng-invalid class to manipulate the DOM
   *
   * @param formGroup
   */
  scrollToFirstInvalidControl(formGroup: FormGroup | FormArray): void {
    setTimeout(() => {
      const firstInvalidControlName = this.findFirstInvalidControl(formGroup);

      if (firstInvalidControlName) {
        const selector = `[formControlName="${firstInvalidControlName}"][class*="ng-invalid"]`;
        const invalidControls = document.querySelectorAll(selector);

        if (invalidControls?.length > 0) {
          const invalidControl = invalidControls[0] as HTMLElement;
          invalidControl.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }
    }, 200);
  }

  /**
   * Find the first invalid control in the nested formControls
   * FormControls inside FormGroup or FormArray
   *
   * @param form
   * @returns
   */
  findFirstInvalidControl(form: FormGroup | FormArray): string | null {
    for (const key in form.controls) {
      if (!form.controls.hasOwnProperty(key)) continue;

      const control = form.get(key);

      if (control instanceof FormGroup || control instanceof FormArray) {
        const nestedInvalid = this.findFirstInvalidControl(control);
        if (nestedInvalid) return nestedInvalid;
      } else if (control && control.invalid) {
        return key;
      }
    }

    return null;
  }

  /**
   * Scroll to first invalid field (in case of ngModel)
   * using the custom-error class to manipulate the DOM
   *
   */
  scrollToFirstInvalidField() {
    setTimeout(() => {
      const invalidFields = document.querySelectorAll('.custom-error');

      if (invalidFields?.length > 0) {
        const firstInvalidField = invalidFields[0] as HTMLElement;
        firstInvalidField.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 200);
  }

  setOrClearRowValidation(form: any, fields: any = []) {
    form.controls.forEach((group: any) => {
      // setting the required validation if there are more than two rows
      this.setOrClearValidationForEachField(
        group as FormGroup,
        fields,
        false,
        form.controls?.length > 1
      );

      // In case of only one row, check for partial filled
      if (form.controls?.length == 1) {
        this.setValidationForGroup(group as FormGroup, fields);
      }
    });
  }

  /**  This method is used to disable / enable keys 
   * Either it's single key
   * Either it's formArray
   * Either it's single object
   * Also Handle index wise disable
   * 
   * here options key value be like this
   * {
    arrayIndex: [1, 3],
    childKeys: 'activityName'
  }
  ***** or *****
  {
    arrayIndex: [1, 3],
    childKeys: ['activityName', 'xyz']
  }

  */

  toggleFormControl(
    form: FormGroup,
    keyName: string,
    isDisabled: boolean,
    options?: {
      // OLD (still supported)
      arrayIndex?: number | number[];
      childKeys?: string | string[];

      // NEW (clearer)
      enable?: {
        arrayIndex?: number | number[];
        childKeys?: string | string[];
      };
      disable?: {
        arrayIndex?: number | number[];
        childKeys?: string | string[];
      };
    }
  ): void {
    const control = form.get(keyName);

    if (!control) {
      console.warn(`Control '${keyName}' not found in form`);
      return;
    }

    // ==============================
    // NEW LOGIC (PRIORITY)
    // ==============================
    if (options?.enable || options?.disable) {
      if (options.enable) {
        this.applyToggleOptions(control, false, options.enable); // enable
      }

      if (options.disable) {
        this.applyToggleOptions(control, true, options.disable); // disable
      }

      return; // stop old logic
    }

    // ==============================
    // OLD LOGIC (UNCHANGED)
    // ==============================
    const childKeys = options?.childKeys
      ? Array.isArray(options.childKeys)
        ? options.childKeys
        : [options.childKeys]
      : null;

    // -------- FormArray --------
    if (control instanceof FormArray) {
      const indexes =
        options?.arrayIndex === undefined
          ? control.controls.map((_, i) => i)
          : Array.isArray(options.arrayIndex)
            ? options.arrayIndex
            : [options.arrayIndex];

      indexes.forEach(index => {
        const rowCtrl = control.at(index);
        if (!rowCtrl) return;

        if (!childKeys) {
          this.toggleAbstractControl(rowCtrl, isDisabled);
          return;
        }

        childKeys.forEach(path => {
          this.toggleByPath(rowCtrl, path, isDisabled);
        });
      });

      return;
    }

    // -------- FormGroup --------
    if (control instanceof FormGroup && childKeys) {
      childKeys.forEach(path => {
        this.toggleByPath(control, path, isDisabled);
      });
      return;
    }

    // -------- FormControl --------
    this.toggleAbstractControl(control, isDisabled);
  }

  private applyToggleOptions(
    control: AbstractControl,
    isDisabled: boolean,
    options: {
      arrayIndex?: number | number[];
      childKeys?: string | string[];
    }
  ): void {
    const childKeys = options.childKeys
      ? Array.isArray(options.childKeys)
        ? options.childKeys
        : [options.childKeys]
      : null;

    // -------- FormArray --------
    if (control instanceof FormArray) {
      const indexes =
        options.arrayIndex === undefined
          ? control.controls.map((_, i) => i)
          : Array.isArray(options.arrayIndex)
            ? options.arrayIndex
            : [options.arrayIndex];

      indexes.forEach(index => {
        const rowCtrl = control.at(index);
        if (!rowCtrl) return;

        if (!childKeys) {
          this.toggleAbstractControl(rowCtrl, isDisabled);
          return;
        }

        childKeys.forEach(path => {
          this.toggleByPath(rowCtrl, path, isDisabled);
        });
      });

      return;
    }

    // -------- FormGroup --------
    if (control instanceof FormGroup && childKeys) {
      childKeys.forEach(path => {
        this.toggleByPath(control, path, isDisabled);
      });
      return;
    }

    // -------- FormControl --------
    this.toggleAbstractControl(control, isDisabled);
  }

  private toggleByPath(
    control: AbstractControl,
    path: string,
    isDisabled: boolean
  ): void {
    const parts = path.split('.');
    let current: AbstractControl | null = control;

    for (const part of parts) {
      if (!current) return;

      if (current instanceof FormArray) {
        current.controls.forEach(ctrl =>
          this.toggleByPath(
            ctrl,
            parts.slice(parts.indexOf(part)).join('.'),
            isDisabled
          )
        );
        return;
      }

      if (current instanceof FormGroup) {
        current = current.get(part);
      }
    }

    if (current) {
      this.toggleAbstractControl(current, isDisabled);
    }
  }

  private toggleAbstractControl(
    control: AbstractControl,
    isDisabled: boolean
  ): void {
    isDisabled
      ? control.disable({ emitEvent: false })
      : control.enable({ emitEvent: false });
  }
}
